import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, FileText, Package } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ErrorReportButton } from '@/components/ErrorReportPanel';
import productApi from '@/lib/productApi';
import { inlineError, type InlineError } from '@/lib/errorReport';
import type { BulkOutcome, BulkPublishImpact } from '@/types/product';

interface BulkPublishDialogProps {
  ids: string[];
  /** Hoeveel van de selectie nog op "nakijken" staat; die markering vervalt hier. */
  reviewCount: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPublished: (outcome: BulkOutcome) => void;
}

/**
 * Publiceren is het enige moment waarop een wijziging zichtbaar wordt voor
 * offertes; bij een selectie geldt dat twaalf keer tegelijk. Daarom eerst de
 * vraag wat je raakt en wat er niet mee kan, en pas dan de knop — met één reden
 * die in de history van alle gepubliceerde producten belandt.
 */
export function BulkPublishDialog({
  ids,
  reviewCount,
  open,
  onOpenChange,
  onPublished,
}: BulkPublishDialogProps) {
  const [impact, setImpact] = useState<BulkPublishImpact | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<InlineError | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setReason('');
    setImpact(null);
    productApi
      .bulkPublishImpact(ids)
      .then(setImpact)
      .catch((caught) => setError(inlineError('De gevolgen konden niet opgehaald worden', caught)));
  }, [open, ids]);

  const publishable = impact?.publishable ?? [];
  const blocked = impact?.blocked ?? [];
  const quotes = impact?.quotes ?? [];
  const bundles = impact?.bundles ?? [];

  const handlePublish = async () => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await productApi.bulk({
        ids: publishable.map((entry) => entry.id),
        action: 'publish',
        reason,
      });
      onOpenChange(false);
      onPublished(outcome);
    } catch (caught) {
      setError(inlineError('Publiceren is niet gelukt', caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {publishable.length || !impact
              ? `${publishable.length || ids.length} producten publiceren`
              : 'Niets om te publiceren'}
          </DialogTitle>
        </DialogHeader>

        {!impact ? (
          <p className="text-sm text-muted-foreground">Gevolgen ophalen…</p>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Ze worden hierna direct kiesbaar in nieuwe offertes. Bestaande offerteregels blijven op
              hun eigen versie staan tot iemand ze bijwerkt.
              {reviewCount > 0 && (
                <>
                  {' '}
                  <span className="text-foreground">
                    {reviewCount === 1 ? 'Eén product staat' : `${reviewCount} producten staan`} nog
                    op &quot;nakijken&quot;; die markering vervalt met deze publicatie.
                  </span>
                </>
              )}
            </p>

            {blocked.length > 0 && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <span className="font-medium">
                    {blocked.length} van de {ids.length} {blocked.length === 1 ? 'wordt' : 'worden'}{' '}
                    overgeslagen.
                  </span>
                  <ul className="mt-1.5 space-y-1">
                    {blocked.slice(0, 5).map((entry) => (
                      <li key={entry.id} className="text-xs">
                        <span className="font-medium">{entry.name}</span> — {entry.reason}
                      </li>
                    ))}
                    {blocked.length > 5 && (
                      <li className="text-xs text-muted-foreground">
                        en nog {blocked.length - 5}
                      </li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <ImpactCard
                icon={<FileText className="h-4 w-4 text-muted-foreground" />}
                count={quotes.length}
                label="lopende offertes gebruiken deze producten"
                detail={quotes
                  .slice(0, 3)
                  .map((quote) => quote.companyName || quote.title)
                  .join(', ')}
              />
              <ImpactCard
                icon={<Package className="h-4 w-4 text-muted-foreground" />}
                count={bundles.length}
                label="bundels hebben er een onderdeel van"
                detail={bundles
                  .slice(0, 3)
                  .map((bundle) => bundle.name)
                  .join(', ')}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="bulk-reason">
                Reden
                <span className="ml-1 font-normal text-muted-foreground">
                  — komt in de historie van alle {publishable.length}
                </span>
              </Label>
              <Textarea
                id="bulk-reason"
                rows={2}
                value={reason}
                placeholder="Bijvoorbeeld: importregels gecontroleerd tegen de tarievensheet"
                onChange={(event) => setReason(event.target.value)}
              />
            </div>

            {error && (
              <p className="text-sm text-destructive">
                {error.message} <ErrorReportButton report={error.report} />
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Annuleren
          </Button>
          <Button onClick={handlePublish} disabled={busy || publishable.length === 0}>
            {busy ? 'Bezig…' : `${publishable.length} publiceren`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImpactCard({
  icon,
  count,
  label,
  detail,
}: {
  icon: ReactNode;
  count: number;
  label: string;
  detail: string;
}) {
  return (
    <div className="rounded-md border bg-muted/40 p-3">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-xl font-semibold tabular-nums">{count}</span>
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">{label}</p>
      {detail && <p className="mt-1 truncate text-xs text-foreground/70">{detail}</p>}
    </div>
  );
}

export default BulkPublishDialog;
